import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import Papa from "papaparse";
import { Company } from "../../models/Company";
import useCompanyService from "../../database/CompanyService";
import useProductService from "../../database/ProductService";
import useProductKeyService from "../../database/ProductKeyService";
import { ProductKey } from "../../models/ProductKey";
import { v4 as uuidv4 } from 'uuid';
import { Timestamp } from "firebase/firestore";
import { Voucher } from "../../models/Voucher";
import { Product } from "../../models/Product";
import useVoucherService from "../../database/VoucherService";

export interface AdministratorPageState {
    isLoading: boolean;
    availableCompanies: Company[];
    selectedCompany: Company | null;

    availableProducts: Product[];
    selectedProduct: Product | null;

    unassignedProductKeys: ProductKey[];
    unassignedVouchers: Voucher[];
    unexportedVouchers: Voucher[];

    requestedNumberOfVouchers: number;

    voucherKeysForExport: {}[];
}

const initialState: AdministratorPageState = {
    isLoading: false,
    selectedCompany: null,
    selectedProduct: null,
    availableCompanies: [],
    availableProducts: [],
    unassignedProductKeys: [],
    unassignedVouchers: [],
    requestedNumberOfVouchers: 0,
    voucherKeysForExport: [],
    unexportedVouchers: [],
}

export const adminPageInitialLoadAsync = createAsyncThunk<void, void, { state: RootState }>(
    'importPage/adminPageInitialLoadAsync',
    async (_, { dispatch, getState, rejectWithValue }) => {
        let appState = getState();
        const importPageState = appState.importPage;

        dispatch(setLoadingState(true));

        var companyService = useCompanyService();

        var companies = await companyService.getCompaniesAsync();
        dispatch(setCompanies(companies));

        dispatch(setLoadingState(false));
        return;
    }
);

export const setCompanyAsync = createAsyncThunk<void, Company, { state: RootState }>(
    'importPage/adminPageInitialLoadAsync',
    async (company: Company, { dispatch, getState, rejectWithValue }) => {
        let appState = getState();
        const importPageState = appState.importPage;

        dispatch(setSelectedCompany(company));

        dispatch(setLoadingState(true));

        var productService = useProductService();

        var productsForCompany = await productService.getProductsByCompanyGuid(company.guid);

        dispatch(setProducts(productsForCompany));
        dispatch(setSelectedProduct(null));

        dispatch(setLoadingState(false));
        return;
    }
);

export const setProductAsync = createAsyncThunk<void, Product, { state: RootState }>(
    'importPage/setProductAsync',
    async (product: Product, { dispatch, getState, rejectWithValue }) => {
        let appState = getState();
        const state = appState.importPage;

        if (state.selectedCompany == null) return;

        dispatch(setSelectedProduct(product));

        dispatch(setLoadingState(true));

        var productKeyService = useProductKeyService();
        var voucherService = useVoucherService();
        let companyGuid = state.selectedCompany!.guid
        var productGuid = product.guid;

        var unassignedProductKeys = await productKeyService.getUnassignedProductKeysAsync(companyGuid, productGuid);
        dispatch(setUnassignedKeys(unassignedProductKeys));

        var unassignedVouchers = await voucherService.getUnassignedVouchersAsync(companyGuid, productGuid);
        dispatch(setUnassignedVouchers(unassignedVouchers));

        var unexportedVouchers = await voucherService.getUnexportedVouchersAsync(companyGuid, productGuid);
        dispatch(setUnexportedVouchers(unexportedVouchers));

        dispatch(setLoadingState(false));
        return;
    }
);

export const importAsync = createAsyncThunk<void, string, { state: RootState }>(
    'importPage/importAsync',
    async (fileContent: string, { dispatch, getState, rejectWithValue }) => {
        let appState = getState();
        const importPageState = appState.importPage;

        if (importPageState.selectedCompany == null || importPageState.selectedProduct == null) return;

        dispatch(setLoadingState(true));

        var parsedKeys = Papa.parse(fileContent, {
            skipEmptyLines: true,
        });

        let importedKeys: string[];
        importedKeys = parsedKeys.data.map((x) => (x as Array<string>)[0] as string);

        var productKeyService = useProductKeyService();

        var existingProductKeys = await productKeyService.getProductKeysByCompanyAndProductAsync(importPageState.selectedCompany!.guid, importPageState.selectedProduct!.guid);

        /*Check for duplicates*/
        var existingKeys: string[] = existingProductKeys.map((x) => x.key);
        let uniqueKeys: string[] = [];
        importedKeys.forEach((x) => {
            if (!existingKeys.includes(x)) uniqueKeys.push(x);
        })

        if (uniqueKeys.length == 0) {
            dispatch(setLoadingState(false));
            alert("All product keys are already imported.");
            return;
        }

        let currentTime = Timestamp.now().toDate();
        let companyGuid = importPageState.selectedCompany!.guid;
        let productGuid = importPageState.selectedProduct!.guid;

        var generatedKeys: ProductKey[] = uniqueKeys.map((key) => {
            let guid = uuidv4();
            return new ProductKey(
                guid,
                null,
                key,
                companyGuid,
                productGuid,
                null,
                null,
                null,
                null,
                null,
                currentTime,
                currentTime,
                null);

        });

        if (generatedKeys.length > 0) {
            await productKeyService.addProductKeyBatchAsync(generatedKeys);
        }

        var availableProductKeys = await productKeyService.getUnassignedProductKeysAsync(companyGuid, productGuid);
        dispatch(setUnassignedKeys(availableProductKeys));

        dispatch(setLoadingState(false));
        alert("Product keys imported successfully.");
        return;
    }
);

export const generateVouchersAsync = createAsyncThunk<void, void, { state: RootState }>(
    'importPage/generateVouchersAsync',
    async (_, { dispatch, getState, rejectWithValue }) => {
        let appState = getState();
        const state = appState.importPage;

        if (state.selectedCompany == null || state.selectedProduct == null) return;

        if (state.requestedNumberOfVouchers == 0) return;

        dispatch(setLoadingState(true));

        let companyGuid = state.selectedCompany!.guid;
        let productGuid = state.selectedProduct!.guid;
        let currentTime = Timestamp.now().toDate();

        var voucherService = useVoucherService();
        var unassignedVouchers = await voucherService.getUnassignedVouchersAsync(companyGuid, productGuid);

        /*Generate a key for each new voucher*/
        var generatedVoucherKeys: string[] = [];
        for (var i = 0; i < state.requestedNumberOfVouchers; i++) {
            generatedVoucherKeys.push(generateVoucherKey(state.selectedCompany!.licensePrependCode));
        }

        if (generatedVoucherKeys.length == 0) {
            alert("No vouchers created");
            dispatch(setLoadingState(false));
            return;
        }

        /*Check for duplicate generated voucher keys */
        var allExistingVouchers = await voucherService.getVouchersByCompanyAndProductAsync(companyGuid, productGuid);

        var existingVoucherKeys: (string | null)[] = allExistingVouchers.map((x) => x.key);
        let uniqueVoucherKeys: string[] = [];
        generatedVoucherKeys.forEach((x) => {
            if (!existingVoucherKeys.includes(x)) {
                uniqueVoucherKeys.push(x);
            }
        })

        if (uniqueVoucherKeys.length == 0) {
            dispatch(setLoadingState(false));
            return;
        }

        var generatedVouchers: Voucher[] = uniqueVoucherKeys.map((uniqueKey) => {
            let guid = uuidv4();
            return new Voucher(
                guid,
                uniqueKey,
                null,
                null,
                false,
                companyGuid,
                productGuid,
                null,
                currentTime,
                currentTime,
                null
            )
        });

        await voucherService.addVoucherBatchAsync(generatedVouchers);

        var unassignedVouchers = await voucherService.getUnassignedVouchersAsync(companyGuid, productGuid);
        dispatch(setUnassignedVouchers(unassignedVouchers));

        dispatch(setLoadingState(false));

        alert(generatedVouchers.length + " vouchers generated successfully");
        return;
    }
);

export const prepareVoucherExportAsync = createAsyncThunk<void, void, { state: RootState }>(
    'importPage/prepareVoucherExportAsync',
    async (_, { dispatch, getState, rejectWithValue }) => {
        let appState = getState();
        const state = appState.importPage;

        if (state.selectedCompany == null || state.selectedProduct == null) return;

        dispatch(setLoadingState(true));

        let companyGuid = state.selectedCompany!.guid;
        let productGuid = state.selectedProduct!.guid;

        var voucherService = useVoucherService();

        var unassignedVouchers = await voucherService.getUnexportedVouchersAsync(companyGuid, productGuid);

        var jsonObject: {}[] = [];
        unassignedVouchers.forEach((x) => {
            var test = { "Voucher Key": x.key ?? "" }
            jsonObject.push(test);
        });

        dispatch(setVoucherKeysForExport(jsonObject))

        /*Set exported date*/
        unassignedVouchers.forEach((x) => x.exportedDate = Timestamp.now().toDate());
        await voucherService.updateVouchersAsync(unassignedVouchers);

        var unexportedVouchers = await voucherService.getUnexportedVouchersAsync(companyGuid, productGuid);
        dispatch(setUnexportedVouchers(unexportedVouchers));

        dispatch(setLoadingState(false));

        return;
    }
);

const maxUInt32Value = 4294967295;
const chars = "9UG814IR05VJT6SPFDNXOKMBL3CWZ7HEQ2AY";
function generateVoucherKey(licensePrependCode: string) {
    let result = "";
    for (let i = 0; i < 6; i++) {
        var random_num = new Uint32Array(1);
        window.crypto.getRandomValues(random_num);

        var scaledRandomValue = random_num[0] / maxUInt32Value;
        result += chars.charAt(Math.floor(scaledRandomValue * chars.length));
    }
    return licensePrependCode + result;
}


export const AdministratorPageSlice = createSlice({
    name: "administratorPage",
    initialState,
    reducers: {
        setLoadingState: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },

        setSelectedCompany: (state, action: PayloadAction<Company | null>) => {
            state.selectedCompany = action.payload;
        },
        setSelectedProduct: (state, action: PayloadAction<Product | null>) => {
            state.selectedProduct = action.payload;
        },
        setCompanies: (state, action: PayloadAction<Company[]>) => {
            state.availableCompanies = action.payload;
        },
        setProducts: (state, action: PayloadAction<Product[]>) => {
            state.availableProducts = action.payload;
        },
        setUnassignedKeys: (state, action: PayloadAction<ProductKey[]>) => {
            state.unassignedProductKeys = action.payload;
        },
        setUnassignedVouchers: (state, action: PayloadAction<Voucher[]>) => {
            state.unassignedVouchers = action.payload;
        },
        setUnexportedVouchers: (state, action: PayloadAction<Voucher[]>) => {
            state.unexportedVouchers = action.payload;
        },
        setVoucherKeysForExport: (state, action: PayloadAction<{}[]>) => {
            state.voucherKeysForExport = action.payload;
        },
        setRequestedNumberOfVouchers: (state, action: PayloadAction<number>) => {
            state.requestedNumberOfVouchers = action.payload;
        },
    }
});

export const {
    setLoadingState,
    setSelectedCompany,
    setCompanies,
    setSelectedProduct,
    setProducts,
    setUnassignedKeys,
    setUnassignedVouchers,
    setVoucherKeysForExport,
    setRequestedNumberOfVouchers,
    setUnexportedVouchers,
} = AdministratorPageSlice.actions;

export const selectState = (state: RootState) => state.importPage;

export default AdministratorPageSlice.reducer;
